import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
var _excluded = ["title", "children", "style", "className"];
import * as React from 'react';
import { useEffect } from 'react';
import KeyCode from "rc-util/es/KeyCode";
import omit from "rc-util/es/omit";
import pickAttrs from "rc-util/es/pickAttrs";
import useMemo from "rc-util/es/hooks/useMemo";
import classNames from 'classnames';
import List from 'rc-virtual-list';
import TransBtn from './TransBtn';
import { fillFieldNames } from './utils/valueUtil';
import { isPlatformMac } from './utils/platformUtil';
import { getTitleFromNode, getUUID, judgeOverflowing, KEY_ALL } from './utils/commonUtil';
/**
 * Using virtual list of option display.
 * Will fallback to dom if use customize render.
 */

var OptionList = function OptionList(_ref, ref) {
  var prefixCls = _ref.prefixCls,
      id = _ref.id,
      fieldNames = _ref.fieldNames,
      flattenOptions = _ref.flattenOptions,
      childrenAsData = _ref.childrenAsData,
      values = _ref.values,
      searchValue = _ref.searchValue,
      multiple = _ref.multiple,
      defaultActiveFirstOption = _ref.defaultActiveFirstOption,
      height = _ref.height,
      itemHeight = _ref.itemHeight,
      notFoundContent = _ref.notFoundContent,
      showAll = _ref.showAll,
      disabledShowAll = _ref.disabledShowAll,
      selectAllText = _ref.selectAllText,
      selectAllIcon = _ref.selectAllIcon,
      open = _ref.open,
      menuItemSelectedIcon = _ref.menuItemSelectedIcon,
      virtual = _ref.virtual,
      autoEllipsis = _ref.autoEllipsis,
      Tooltip = _ref.tooltip,
      onSelect = _ref.onSelect,
      onSelectAll = _ref.onSelectAll,
      onToggleOpen = _ref.onToggleOpen,
      onActiveValue = _ref.onActiveValue,
      onScroll = _ref.onScroll,
      onMouseEnter = _ref.onMouseEnter;
  var uuidRef = React.useRef(getUUID());
  var itemPrefixCls = "".concat(prefixCls, "-item"); // const memoFlattenOptions = useMemo(
  //   () => flattenOptions,
  //   [open, flattenOptions],
  //   (prev, next) => next[0] && prev[1] !== next[1],
  // );
  // 虚拟列表增加全部按钮

  var memoFlattenOptions = useMemo(function () {
    if (multiple && flattenOptions.length && showAll) {
      var data = {
        key: KEY_ALL,
        disabled: disabledShowAll || flattenOptions.every(function (item) {
          var _item$data;

          return (_item$data = item.data) === null || _item$data === void 0 ? void 0 : _item$data.disabled;
        }),
        value: KEY_ALL,
        label: selectAllText
      };
      return [{
        key: KEY_ALL,
        data: data
      }].concat(_toConsumableArray(flattenOptions));
    }

    return flattenOptions;
  }, [open, flattenOptions], function (prev, next) {
    return next[0] && prev[1] !== next[1];
  }); // =========================== List ===========================

  var listRef = React.useRef(null);

  var onListMouseDown = function onListMouseDown(event) {
    event.preventDefault();
  };

  var scrollIntoView = function scrollIntoView(index) {
    if (listRef.current) {
      listRef.current.scrollTo({
        index: index
      });
    }
  }; // ========================== Active ==========================


  var getEnabledActiveIndex = function getEnabledActiveIndex(index) {
    var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
    var len = memoFlattenOptions.length;

    for (var i = 0; i < len; i += 1) {
      var current = (index + i * offset + len) % len;
      var _memoFlattenOptions$c = memoFlattenOptions[current],
          group = _memoFlattenOptions$c.group,
          data = _memoFlattenOptions$c.data;

      if (!group && !data.disabled) {
        return current;
      }
    }

    return -1;
  };

  var _React$useState = React.useState(function () {
    return getEnabledActiveIndex(0);
  }),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      activeIndex = _React$useState2[0],
      setActiveIndex = _React$useState2[1];

  var setActive = function setActive(index) {
    var fromKeyboard = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    setActiveIndex(index);
    var info = {
      source: fromKeyboard ? 'keyboard' : 'mouse'
    }; // Trigger active event

    var flattenItem = memoFlattenOptions[index];

    if (!flattenItem) {
      onActiveValue(null, -1, info);
      return;
    }

    onActiveValue(flattenItem.data.value, index, info);
  }; // Auto active first item when list length or searchValue changed


  useEffect(function () {
    setActive(defaultActiveFirstOption !== false ? getEnabledActiveIndex(0) : -1);
  }, [memoFlattenOptions.length, searchValue]); // Auto scroll to item position in single mode

  useEffect(function () {
    /**
     * React will skip `onChange` when component update.
     * `setActive` function will call root accessibility state update which makes re-render.
     * So we need to delay to let Input component trigger onChange first.
     */
    var timeoutId = setTimeout(function () {
      if (!multiple && open && values.size === 1) {
        var value = Array.from(values)[0];
        var index = memoFlattenOptions.findIndex(function (_ref2) {
          var data = _ref2.data;
          return data.value === value;
        });

        if (index !== -1) {
          setActive(index);
          scrollIntoView(index);
        }
      }
    }); // Force trigger scrollbar visible when open

    if (open) {
      var _listRef$current;

      (_listRef$current = listRef.current) === null || _listRef$current === void 0 ? void 0 : _listRef$current.scrollTo(undefined);
    }

    return function () {
      return clearTimeout(timeoutId);
    };
  }, [open, searchValue]); // ========================== Values ==========================

  var onSelectValue = function onSelectValue(value) {
    if (value !== undefined) {
      onSelect(value, {
        selected: !values.has(value)
      });
    } // Single mode should always close by select


    if (!multiple) {
      onToggleOpen(false);
    }
  }; // 全选按钮状态


  var getCheckStatus = function getCheckStatus() {
    var status = 'none';

    if (values.size === 0) {
      return status;
    }

    var optValues = memoFlattenOptions.filter(function (item) {
      return item.key !== KEY_ALL && !item.data.disabled;
    }).map(function (item) {
      return item.value;
    }).filter(Boolean);

    if (optValues.some(function (k) {
      return values.has(k);
    })) {
      status = 'part';
    }

    if (optValues.every(function (k) {
      return values.has(k);
    })) {
      status = 'all';
    }

    return status;
  }; // 全选


  var onSelectValueAll = function onSelectValueAll() {
    var newValues = memoFlattenOptions.filter(function (v) {
      return v.value !== undefined && !v.data.disabled;
    }).map(function (item) {
      return item.value;
    });

    if (newValues && newValues.length) {
      onSelectAll(newValues, {
        selected: getCheckStatus() !== 'all'
      });
    }
  }; // ========================= Keyboard =========================


  React.useImperativeHandle(ref, function () {
    return {
      onKeyDown: function onKeyDown(event) {
        var which = event.which,
            ctrlKey = event.ctrlKey;

        switch (which) {
          // >>> Arrow keys & ctrl + n/p on Mac
          case KeyCode.N:
          case KeyCode.P:
          case KeyCode.UP:
          case KeyCode.DOWN:
            {
              var offset = 0;

              if (which === KeyCode.UP) {
                offset = -1;
              } else if (which === KeyCode.DOWN) {
                offset = 1;
              } else if (isPlatformMac() && ctrlKey) {
                if (which === KeyCode.N) {
                  offset = 1;
                } else if (which === KeyCode.P) {
                  offset = -1;
                }
              }

              if (offset !== 0) {
                var nextActiveIndex = getEnabledActiveIndex(activeIndex + offset, offset);
                scrollIntoView(nextActiveIndex);
                setActive(nextActiveIndex, true);
              }

              break;
            }
          // >>> Select

          case KeyCode.ENTER:
            {
              // value
              var item = memoFlattenOptions[activeIndex];

              if (item && !item.data.disabled && item.key !== KEY_ALL) {
                onSelectValue(item.data.value);
              } else {
                onSelectValue(undefined);
              }

              if (open) {
                event.preventDefault();
              }

              break;
            }
          // >>> Close

          case KeyCode.ESC:
            {
              onToggleOpen(false);

              if (open) {
                event.stopPropagation();
              }
            }
        }
      },
      onKeyUp: function onKeyUp() {},
      scrollTo: function scrollTo(index) {
        scrollIntoView(index);
      }
    };
  }); // ========================== Render ==========================

  var SelectAllIcon = selectAllIcon;

  if (memoFlattenOptions.length === 0) {
    return /*#__PURE__*/React.createElement("div", {
      role: "listbox",
      id: "".concat(id, "_list"),
      className: "".concat(itemPrefixCls, "-empty"),
      onMouseDown: onListMouseDown
    }, notFoundContent);
  }

  var filledFieldNames = fillFieldNames(fieldNames);
  var omitFieldNameList = Object.keys(filledFieldNames).map(function (key) {
    return filledFieldNames[key];
  });

  var renderItem = function renderItem(index) {
    var item = memoFlattenOptions[index];
    if (!item) return null;
    var itemData = item.data || {};
    var value = itemData.value,
        label = itemData.label,
        children = itemData.children;
    var attrs = pickAttrs(itemData, true);
    var mergedLabel = childrenAsData ? children : label;
    return item ? /*#__PURE__*/React.createElement("div", _extends({
      "aria-label": typeof mergedLabel === 'string' ? mergedLabel : null
    }, attrs, {
      key: index,
      role: "option",
      id: "".concat(id, "_list_").concat(index),
      "aria-selected": values.has(value)
    }), value) : null;
  };

  var handleScroll = function handleScroll(event) {
    document.querySelectorAll(".".concat(prefixCls, "-item-content-tooltip")).forEach(function (node) {
      if (node) {
        node.style.display = 'none';
      }
    });
    onScroll === null || onScroll === void 0 ? void 0 : onScroll(event);
  };

  function highlightKeyword(str, lowerKeyword) {
    var cells = str.toLowerCase().split(lowerKeyword).reduce(function (list, cur, index) {
      return index === 0 ? [cur] : [].concat(_toConsumableArray(list), [lowerKeyword, cur]);
    }, []);
    var fillCells = [];
    var start = 0;
    cells.forEach(function (cell, index) {
      var end = start + cell.length;
      var originWorld = str.slice(start, end);
      start = end;

      if (index % 2 === 1) {
        originWorld = /*#__PURE__*/React.createElement("span", {
          className: "".concat(itemPrefixCls, "-option-content-bold"),
          key: "".concat(cell, "-").concat(index, "-seperator")
        }, originWorld);
      }

      fillCells.push(originWorld);
    });
    return fillCells;
  }

  function highlightSearch(content) {
    var lower = String(searchValue || '').toLowerCase();
    var label = content;

    var type = _typeof(label);

    if (lower && (type === 'string' || type === 'number')) {
      label = highlightKeyword(String(label), lower);
    }

    return label;
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    role: "listbox",
    id: "".concat(id, "_list"),
    style: {
      height: 0,
      width: 0,
      overflow: 'hidden'
    }
  }, renderItem(activeIndex - 1), renderItem(activeIndex), renderItem(activeIndex + 1)), /*#__PURE__*/React.createElement(List, {
    itemKey: "key",
    ref: listRef,
    data: memoFlattenOptions,
    height: height,
    itemHeight: itemHeight,
    fullHeight: false,
    onMouseDown: onListMouseDown,
    onScroll: handleScroll,
    virtual: virtual,
    onMouseEnter: onMouseEnter
  }, function (_ref3, itemIndex) {
    var _classNames2;

    var group = _ref3.group,
        groupOption = _ref3.groupOption,
        data = _ref3.data,
        label = _ref3.label,
        value = _ref3.value;
    var key = data.key,
        disabled = data.disabled; // Group

    if (group) {
      return /*#__PURE__*/React.createElement("div", {
        className: classNames(itemPrefixCls, "".concat(itemPrefixCls, "-group"))
      }, label !== undefined ? label : key);
    } // 全部按钮


    if (key === KEY_ALL) {
      var _classNames;

      return /*#__PURE__*/React.createElement("div", {
        "aria-selected": getCheckStatus() !== 'none',
        className: classNames(itemPrefixCls, "".concat(itemPrefixCls, "-all"), "".concat(itemPrefixCls, "-option"), (_classNames = {}, _defineProperty(_classNames, "".concat(itemPrefixCls, "-option-selected"), getCheckStatus() !== 'none'), _defineProperty(_classNames, "".concat(itemPrefixCls, "-option-active"), activeIndex === itemIndex && !disabled), _defineProperty(_classNames, "".concat(itemPrefixCls, "-option-disabled"), disabled), _classNames)) // title={data.label.toString()}
        ,
        onMouseMove: function onMouseMove() {
          if (activeIndex === itemIndex) {
            return;
          }

          setActive(itemIndex);
        },
        onClick: onSelectValueAll
      }, /*#__PURE__*/React.createElement("span", {
        className: "".concat(itemPrefixCls, "-option-state-multiple")
      }, SelectAllIcon ? /*#__PURE__*/React.createElement(SelectAllIcon, {
        checked: getCheckStatus() === 'all',
        indeterminate: getCheckStatus() === 'part',
        disabled: disabled
      }) : null), /*#__PURE__*/React.createElement("div", {
        className: "".concat(itemPrefixCls, "-option-content")
      }, data.label));
    }

    var ellipsisConfig = autoEllipsis === true ? {} : autoEllipsis;
    var isOverflowing = judgeOverflowing(document.getElementById("content-".concat(itemIndex, "-").concat(uuidRef.current)));

    var title = data.title,
        children = data.children,
        style = data.style,
        className = data.className,
        otherProps = _objectWithoutProperties(data, _excluded);

    var passedProps = omit(otherProps, omitFieldNameList); // Option

    var selected = values.has(value);
    var optionPrefixCls = "".concat(itemPrefixCls, "-option");
    var optionClassName = classNames(itemPrefixCls, optionPrefixCls, className, (_classNames2 = {}, _defineProperty(_classNames2, "".concat(optionPrefixCls, "-grouped"), groupOption), _defineProperty(_classNames2, "".concat(optionPrefixCls, "-active"), activeIndex === itemIndex && !disabled), _defineProperty(_classNames2, "".concat(optionPrefixCls, "-disabled"), disabled), _defineProperty(_classNames2, "".concat(optionPrefixCls, "-selected"), selected), _classNames2));
    var mergedLabel = childrenAsData ? children : label;
    var iconVisible = !menuItemSelectedIcon || typeof menuItemSelectedIcon === 'function' || selected;
    var content = mergedLabel || value; // https://github.com/ant-design/ant-design/issues/26717

    var optionTitle = getTitleFromNode(content, title);
    return /*#__PURE__*/React.createElement("div", _extends({}, passedProps, {
      "aria-selected": selected,
      className: optionClassName,
      onMouseMove: function onMouseMove() {
        if (activeIndex === itemIndex || disabled) {
          return;
        }

        setActive(itemIndex);
      },
      onClick: function onClick() {
        if (!disabled) {
          onSelectValue(value);
        }
      },
      style: style
    }), multiple && selectAllIcon && /*#__PURE__*/React.createElement("span", {
      className: "".concat(itemPrefixCls, "-option-state-multiple")
    }, /*#__PURE__*/React.createElement(SelectAllIcon, {
      checked: selected,
      disabled: disabled
    })), /*#__PURE__*/React.createElement(!disabled && ellipsisConfig && isOverflowing && optionTitle && Tooltip ? Tooltip : React.Fragment, !disabled && ellipsisConfig && isOverflowing && optionTitle && Tooltip ? _objectSpread({
      title: optionTitle,
      overlayClassName: "".concat(prefixCls, "-item-content-tooltip"),
      destroyTooltipOnHide: true
    }, ellipsisConfig) : null, /*#__PURE__*/React.createElement("div", {
      id: "content-".concat(itemIndex, "-").concat(uuidRef.current),
      className: classNames("".concat(optionPrefixCls, "-content"), _defineProperty({}, "".concat(optionPrefixCls, "-content-overflowing"), isOverflowing))
    }, highlightSearch(content))), /*#__PURE__*/React.isValidElement(menuItemSelectedIcon) || selected, !multiple && iconVisible && /*#__PURE__*/React.createElement(TransBtn, {
      className: "".concat(itemPrefixCls, "-option-state"),
      customizeIcon: menuItemSelectedIcon,
      customizeIconProps: {
        isSelected: selected
      }
    }, selected ? '✓' : null));
  }));
};

var RefOptionList = /*#__PURE__*/React.forwardRef(OptionList);
RefOptionList.displayName = 'OptionList';
export default RefOptionList;